import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ChevronLeft as ChevronLeftIcon } from "@mui/icons-material";
import { CustomCircleIconButton, colors } from "@ippon/shared-components";
import {
  SIDEBAR_TRANSITION_TIMING,
  useSidebarWidth,
} from "@ippon/shared-layouts";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getChatThreads } from "@/store/chatSlice";
import { HEADER_HEIGHT } from "@/constants/layout";
import CustomThreadSidebar from "@/components/AIChatbot/CustomThreadSidebar";

export const HOME_HISTORY_PANEL_WIDTH = 280;

interface HomeChatHistoryProps {
  open: boolean;
  onClose: () => void;
  /** 押した位置を、全画面チャットが開くアニメーションの起点として渡す。 */
  onSelectThread: (threadId: string, origin: { x: number; y: number }) => void;
}

/**
 * ホームの会話履歴パネル。左サイドバーの右隣に固定し、サイドバーの縮小と同じ速さで出し入れする。
 * 閉じている間もマウントしたまま、背面（サイドバーの下）へ引っ込める。
 */
export const HomeChatHistory: React.FC<HomeChatHistoryProps> = ({
  open,
  onClose,
  onSelectThread,
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const sidebarWidth = useSidebarWidth();
  const [selectedThreadId, setSelectedThreadId] = useState<string>();
  const lastClickRef = useRef({ x: 0, y: 0 });
  const { threads, isLoadingThreads, threadsHasMore, threadsNextToken } =
    useAppSelector((state) => state.chat);

  useEffect(() => {
    if (open) dispatch(getChatThreads({ limit: 15 }));
  }, [open, dispatch]);

  const handleLoadMore = useCallback(() => {
    if (threadsHasMore && !isLoadingThreads && threadsNextToken) {
      dispatch(getChatThreads({ limit: 15, next_token: threadsNextToken }));
    }
  }, [threadsHasMore, isLoadingThreads, threadsNextToken, dispatch]);

  const handleSelectThread = (threadId: string) => {
    setSelectedThreadId(threadId);
    onSelectThread(threadId, lastClickRef.current);
  };

  return (
    <Box
      component="aside"
      aria-label="チャット履歴"
      aria-hidden={!open}
      data-testid="home-chat-history"
      onClickCapture={(event: React.MouseEvent) => {
        lastClickRef.current = { x: event.clientX, y: event.clientY };
      }}
      sx={{
        position: "fixed",
        top: HEADER_HEIGHT,
        bottom: 0,
        left: sidebarWidth,
        width: isPhone ? "100%" : HOME_HISTORY_PANEL_WIDTH,
        // サイドバー(drawer)より下・ヘッダー(appBar)より下に置き、サイドバーの裏から出てくるように見せる。
        zIndex: theme.zIndex.appBar - 1,
        display: "flex",
        flexDirection: "column",
        backgroundColor: colors.background.paper,
        // ヘッダー（パンくず）と履歴の境目を示す。
        borderTop: `1px solid ${colors.border.tab}`,
        borderRight: `1px solid ${colors.border.tab}`,
        transform: open ? "translateX(0)" : "translateX(-100%)",
        visibility: open ? "visible" : "hidden",
        transition: [
          `transform ${SIDEBAR_TRANSITION_TIMING}`,
          `left ${SIDEBAR_TRANSITION_TIMING}`,
          // 閉じるときは引っ込み終わってから非表示にする。
          `visibility 0s linear ${open ? "0s" : "0.2s"}`,
        ].join(", "),
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 48,
          pl: 2,
          pr: 1,
          borderBottom: `1px solid ${colors.border.tab}`,
        }}
      >
        <Typography
          component="h2"
          sx={{ fontSize: 14, fontWeight: 700, color: colors.text.primary }}
        >
          履歴
        </Typography>
        <CustomCircleIconButton ariaLabel="履歴を閉じる" onClick={onClose}>
          <ChevronLeftIcon sx={{ fontSize: 24 }} />
        </CustomCircleIconButton>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          "& > div": { borderRight: "none", borderBottom: "none" },
        }}
      >
        <CustomThreadSidebar
          threads={threads}
          selectedThreadId={selectedThreadId}
          onThreadSelect={handleSelectThread}
          isLoading={isLoadingThreads}
          hasMore={threadsHasMore}
          onLoadMore={handleLoadMore}
          width={0}
          padding="12px 16px"
          useTruncateText
          previewMaxLength={null}
        />
      </Box>
    </Box>
  );
};

export default HomeChatHistory;
